/**
 * HustleMeetHostDashboardPage.jsx — /meet/host/mine
 *
 * "My Circles" workspace for approved hosts:
 *   - See each Circle (status + upcoming dinners)
 *   - Create a new Circle (name + venue + cadence)
 *   - Publish the next dinner date on an existing Circle
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Plus, Users, CalendarPlus, ArrowRight, Sparkles,
         MapPin, CircleDot, Link2, Link2Off } from "lucide-react";
import PlatformHeader from "@/components/PlatformHeader";
import SuiteFooter from "@/components/SuiteFooter";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const authHeader = () => {
  const t = localStorage.getItem("token") || localStorage.getItem("access_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const CAD_OPTIONS = ["weekly", "fortnightly", "monthly", "adhoc"];
const FEAST_OPTIONS = [
  ["per_event",     "Per event"],
  ["member_picks",  "Member pre-selects"],
  ["surprise_menu", "Surprise Menu (chef picks)"],
];

function StatusPill({ status }) {
  const map = {
    active:         ["Active",           "bg-emerald-500/10 text-emerald-500"],
    pending_review: ["Pending review",   "bg-amber-500/10 text-amber-500"],
    paused:         ["Paused",           "bg-stone-500/10 text-stone-500"],
    archived:       ["Archived",         "bg-stone-500/10 text-stone-500"],
  };
  const [lbl, cls] = map[status] || [status, "bg-stone-500/10 text-stone-500"];
  return (
    <span
      data-testid={`circle-status-${status}`}
      className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-black px-2.5 py-1 rounded-full ${cls}`}
    >
      <CircleDot size={10}/> {lbl}
    </span>
  );
}

function CreateCircleForm({ onCreated }) {
  const [name,      setName]      = useState("");
  const [vendorId,  setVendorId]  = useState("");
  const [tagline,   setTagline]   = useState("");
  const [cadence,   setCadence]   = useState("weekly");
  const [partySize, setPartySize] = useState(6);
  const [busy,      setBusy]      = useState(false);

  const submit = async () => {
    if (!name.trim() || !vendorId.trim()) {
      toast.error("Circle name and vendor id are required.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/hustlemeet-circles`,
        {
          name, vendor_id: vendorId, tagline,
          cadence, default_party_size: Number(partySize) || 6,
        },
        { headers: authHeader(), withCredentials: true });
      toast.success("Circle created — awaiting admin review.");
      onCreated(data);
      setName(""); setVendorId(""); setTagline(""); setPartySize(6);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="create-circle-form"
      className="rounded-2xl border border-amber-300 dark:border-amber-800 bg-gradient-to-br from-amber-400/5 to-orange-500/5 p-6 mb-6"
    >
      <h2 className="font-black text-lg mb-1">Create a new Circle</h2>
      <p className="text-xs text-stone-500 mb-5">
        Once created, admin reviews the venue before you can publish the first dinner.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-[11px] uppercase tracking-widest text-stone-500 font-bold">Name</label>
          <input
            data-testid="input-circle-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="mt-1 w-full h-11 rounded-lg border border-stone-200 dark:border-stone-700 bg-white dark:bg-slate-900 px-3 text-sm"
            placeholder="e.g. Sunday Suppers · South Bank"
          />
        </div>
        <div>
          <label className="block text-[11px] uppercase tracking-widest text-stone-500 font-bold">Vendor ID</label>
          <input
            data-testid="input-circle-vendor-id"
            value={vendorId}
            onChange={(e) => setVendorId(e.target.value)}
            className="mt-1 w-full h-11 rounded-lg border border-stone-200 dark:border-stone-700 bg-white dark:bg-slate-900 px-3 text-sm"
            placeholder="business.id from Hustle vendors"
          />
        </div>
        <div className="sm:col-span-2">
          <label className="block text-[11px] uppercase tracking-widest text-stone-500 font-bold">Tagline (optional)</label>
          <input
            data-testid="input-circle-tagline"
            value={tagline}
            onChange={(e) => setTagline(e.target.value)}
            className="mt-1 w-full h-11 rounded-lg border border-stone-200 dark:border-stone-700 bg-white dark:bg-slate-900 px-3 text-sm"
            placeholder="Slow food · warm strangers · Sunday nights"
          />
        </div>
        <div>
          <label className="block text-[11px] uppercase tracking-widest text-stone-500 font-bold">Cadence</label>
          <select
            data-testid="select-circle-cadence"
            value={cadence}
            onChange={(e) => setCadence(e.target.value)}
            className="mt-1 w-full h-11 rounded-lg border border-stone-200 dark:border-stone-700 bg-white dark:bg-slate-900 px-3 text-sm"
          >
            {CAD_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[11px] uppercase tracking-widest text-stone-500 font-bold">Default party size</label>
          <input
            data-testid="input-circle-party-size"
            type="number" min="2" max="20"
            value={partySize}
            onChange={(e) => setPartySize(e.target.value)}
            className="mt-1 w-full h-11 rounded-lg border border-stone-200 dark:border-stone-700 bg-white dark:bg-slate-900 px-3 text-sm"
          />
        </div>
      </div>
      <div className="mt-5 flex justify-end">
        <Button
          data-testid="btn-create-circle"
          onClick={submit}
          disabled={busy || !name.trim() || !vendorId.trim()}
          className="rounded-full bg-amber-500 text-slate-950 hover:bg-amber-400 font-black h-11 px-6"
        >
          <Plus size={14} className="mr-1"/>
          {busy ? "Creating…" : "Create Circle"}
        </Button>
      </div>
    </div>
  );
}

function PublishDinnerForm({ circle, onPublished }) {
  const [title,    setTitle]    = useState("");
  const [startsAt, setStartsAt] = useState("");
  const [price,    setPrice]    = useState(85);
  const [capacity, setCapacity] = useState(circle.default_party_size || 6);
  const [feast,    setFeast]    = useState("per_event");
  const [menuUrl,  setMenuUrl]  = useState("");
  const [importing,setImporting]= useState(false);
  const [busy,     setBusy]     = useState(false);

  const importVendorMenu = async () => {
    setImporting(true);
    try {
      const { data } = await axios.get(
        `${API}/hustlemeet-circles/host/${circle.id}/vendor-menu`,
        { headers: authHeader(), withCredentials: true });
      const url = data?.menu_url || "";
      if (!url) {
        toast.error("This vendor hasn't uploaded a menu yet.");
      } else {
        setMenuUrl(url);
        toast.success("Vendor menu imported — attaching to this dinner.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail
        || "Couldn't reach the vendor record.");
    } finally {
      setImporting(false);
    }
  };

  const submit = async () => {
    if (!startsAt) { toast.error("Pick a start date/time."); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/hustlemeet-circles/host/${circle.id}/events`,
        {
          title: title || circle.name,
          starts_at: new Date(startsAt).toISOString(),
          general_price_cents: Math.round(Number(price) * 100),
          general_capacity:    Number(capacity) || 6,
          feast_select_mode:   feast,
          menu_url:            menuUrl || undefined,
        },
        { headers: authHeader(), withCredentials: true });
      toast.success("Dinner published — bookings open.");
      onPublished(data);
      setStartsAt("");
      setMenuUrl("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Publish failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid={`publish-dinner-form-${circle.id}`}
      className="mt-4 rounded-xl border border-amber-300 dark:border-amber-800 bg-amber-500/5 p-4"
    >
      <p className="text-[11px] uppercase tracking-widest text-amber-500 font-black inline-flex items-center gap-1">
        <CalendarPlus size={11}/> Publish next dinner
      </p>
      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
        <input
          data-testid={`input-dinner-title-${circle.id}`}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="h-11 rounded-lg border border-stone-200 dark:border-stone-700 bg-white dark:bg-slate-900 px-3 text-sm"
          placeholder={`Title (default: ${circle.name})`}
        />
        <input
          data-testid={`input-dinner-starts-at-${circle.id}`}
          type="datetime-local"
          value={startsAt}
          onChange={(e) => setStartsAt(e.target.value)}
          className="h-11 rounded-lg border border-stone-200 dark:border-stone-700 bg-white dark:bg-slate-900 px-3 text-sm"
        />
        <input
          data-testid={`input-dinner-price-${circle.id}`}
          type="number" min="0" step="1"
          value={price}
          onChange={(e) => setPrice(e.target.value)}
          className="h-11 rounded-lg border border-stone-200 dark:border-stone-700 bg-white dark:bg-slate-900 px-3 text-sm"
          placeholder="Price per seat (AUD)"
        />
        <input
          data-testid={`input-dinner-capacity-${circle.id}`}
          type="number" min="2" max="20"
          value={capacity}
          onChange={(e) => setCapacity(e.target.value)}
          className="h-11 rounded-lg border border-stone-200 dark:border-stone-700 bg-white dark:bg-slate-900 px-3 text-sm"
          placeholder="Capacity"
        />
        <select
          data-testid={`select-dinner-feast-${circle.id}`}
          value={feast}
          onChange={(e) => setFeast(e.target.value)}
          className="sm:col-span-2 h-11 rounded-lg border border-stone-200 dark:border-stone-700 bg-white dark:bg-slate-900 px-3 text-sm"
        >
          {FEAST_OPTIONS.map(([v, l]) =>
            <option key={v} value={v}>Feast mode: {l}</option>)}
        </select>
        <div className="sm:col-span-2 flex flex-col sm:flex-row gap-2">
          <input
            data-testid={`input-dinner-menu-url-${circle.id}`}
            value={menuUrl}
            onChange={(e) => setMenuUrl(e.target.value)}
            className="flex-1 h-11 rounded-lg border border-stone-200 dark:border-stone-700 bg-white dark:bg-slate-900 px-3 text-sm"
            placeholder="Menu URL (PDF or web page) — optional"
          />
          <Button
            type="button"
            data-testid={`btn-import-vendor-menu-${circle.id}`}
            onClick={importVendorMenu}
            disabled={importing}
            className="rounded-lg bg-stone-200 text-stone-900 hover:bg-stone-300 dark:bg-stone-800 dark:text-stone-100 dark:hover:bg-stone-700 h-11 px-4 text-xs font-black whitespace-nowrap"
          >
            {importing ? "Importing…" : "↓ Import vendor menu"}
          </Button>
        </div>
      </div>
      <div className="mt-3 flex justify-end">
        <Button
          data-testid={`btn-publish-dinner-${circle.id}`}
          onClick={submit}
          disabled={busy || !startsAt}
          className="rounded-full bg-amber-500 text-slate-950 hover:bg-amber-400 font-black"
        >
          {busy ? "Publishing…" : "Publish dinner"}
        </Button>
      </div>
    </div>
  );
}

function AttachMeetGroupPanel({ circle, onCircleUpdated }) {
  const alreadyAttached = !!circle.parent_meet_group_id;
  const rules = circle.attach_unlock_rules || {
    min_rsvps: 3, min_turn_ups: 2, min_reliability: 4.0, open_to_outside: true,
  };
  const [open,     setOpen]     = useState(false);
  const [mgId,     setMgId]     = useState(circle.parent_meet_group_id || "");
  const [rsvps,    setRsvps]    = useState(rules.min_rsvps);
  const [turnUps,  setTurnUps]  = useState(rules.min_turn_ups);
  const [rel,      setRel]      = useState(rules.min_reliability);
  const [openOut,  setOpenOut]  = useState(rules.open_to_outside);
  const [busy,     setBusy]     = useState(false);

  const attach = async () => {
    if (!mgId.trim()) { toast.error("Meet group ID required"); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/hustlemeet-circles/host/${circle.id}/attach-meet-group`,
        { meet_group_id: mgId.trim(),
          unlock_rules: {
            min_rsvps:       Number(rsvps),
            min_turn_ups:    Number(turnUps),
            min_reliability: Number(rel),
            open_to_outside: !!openOut,
          } },
        { headers: authHeader(), withCredentials: true });
      toast.success("Attached — Meet-group members can now discover this Circle.");
      onCircleUpdated?.({
        parent_meet_group_id: data.meet_group_id,
        attach_unlock_rules:  data.unlock_rules,
      });
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Attach failed");
    } finally { setBusy(false); }
  };

  const detach = async () => {
    if (!window.confirm("Detach this Circle from its Meet group?")) return;
    setBusy(true);
    try {
      await axios.delete(
        `${API}/hustlemeet-circles/host/${circle.id}/attach-meet-group`,
        { headers: authHeader(), withCredentials: true });
      toast.success("Detached — Circle is now standalone.");
      onCircleUpdated?.({
        parent_meet_group_id: null,
        attach_unlock_rules:  null,
      });
      setMgId("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Detach failed");
    } finally { setBusy(false); }
  };

  return (
    <div
      data-testid={`attach-panel-${circle.id}`}
      className="mt-4 pt-4 border-t border-stone-200 dark:border-stone-800"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {alreadyAttached
            ? <Link2 size={14} className="text-orange-500"/>
            : <Link2Off size={14} className="text-stone-400"/>}
          <span className="text-[11px] uppercase tracking-widest font-black text-stone-600 dark:text-stone-300">
            {alreadyAttached
              ? <>Attached to Meet group <code className="ml-1 text-orange-500 normal-case tracking-normal">{circle.parent_meet_group_id}</code></>
              : "Not attached to a Meet group"}
          </span>
        </div>
        <button
          type="button"
          onClick={() => setOpen(!open)}
          data-testid={`btn-toggle-attach-${circle.id}`}
          className="text-xs font-bold text-amber-500 hover:underline"
        >
          {open ? "Hide" : (alreadyAttached ? "Edit" : "Attach")}
        </button>
      </div>

      {open && (
        <div className="mt-3 rounded-xl border border-orange-200 dark:border-orange-900/50 bg-orange-500/5 p-4 space-y-3">
          <div>
            <label className="block text-[11px] uppercase tracking-widest text-stone-500 font-bold">
              Meet group ID or slug
            </label>
            <input
              data-testid={`input-meet-group-id-${circle.id}`}
              value={mgId}
              onChange={(e) => setMgId(e.target.value)}
              className="mt-1 w-full h-10 rounded-lg border border-stone-200 dark:border-stone-700 bg-white dark:bg-slate-900 px-3 text-sm"
              placeholder="e.g. sydney-foodies · hmg_abc123"
            />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-stone-500 font-bold">Min RSVPs</label>
              <input
                data-testid={`input-rules-rsvps-${circle.id}`}
                type="number" min="0"
                value={rsvps}
                onChange={(e) => setRsvps(e.target.value)}
                className="mt-1 w-full h-10 rounded-lg border border-stone-200 dark:border-stone-700 bg-white dark:bg-slate-900 px-3 text-sm"
              />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-stone-500 font-bold">Min turn-ups</label>
              <input
                data-testid={`input-rules-turnups-${circle.id}`}
                type="number" min="0"
                value={turnUps}
                onChange={(e) => setTurnUps(e.target.value)}
                className="mt-1 w-full h-10 rounded-lg border border-stone-200 dark:border-stone-700 bg-white dark:bg-slate-900 px-3 text-sm"
              />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-stone-500 font-bold">Min reliability (0-5)</label>
              <input
                data-testid={`input-rules-reliability-${circle.id}`}
                type="number" min="0" max="5" step="0.1"
                value={rel}
                onChange={(e) => setRel(e.target.value)}
                className="mt-1 w-full h-10 rounded-lg border border-stone-200 dark:border-stone-700 bg-white dark:bg-slate-900 px-3 text-sm"
              />
            </div>
          </div>
          <label className="flex items-center gap-2 text-xs text-stone-600 dark:text-stone-300">
            <input
              type="checkbox"
              data-testid={`checkbox-open-outside-${circle.id}`}
              checked={openOut}
              onChange={(e) => setOpenOut(e.target.checked)}
            />
            Also accept applications from members outside the Meet group
          </label>
          <div className="flex justify-end gap-2 pt-1">
            {alreadyAttached && (
              <Button
                type="button"
                data-testid={`btn-detach-${circle.id}`}
                onClick={detach}
                disabled={busy}
                className="rounded-full bg-stone-200 text-stone-900 hover:bg-stone-300 dark:bg-stone-800 dark:text-stone-100 dark:hover:bg-stone-700 h-9 px-4 text-xs font-black"
              >
                Detach
              </Button>
            )}
            <Button
              type="button"
              data-testid={`btn-attach-${circle.id}`}
              onClick={attach}
              disabled={busy || !mgId.trim()}
              className="rounded-full bg-orange-500 text-white hover:bg-orange-600 h-9 px-4 text-xs font-black"
            >
              {busy ? "Saving…" : (alreadyAttached ? "Save changes" : "Attach")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function CircleRow({ c, onEventPublished, onCircleUpdated }) {
  const [events, setEvents] = useState(null);
  const [show,   setShow]   = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(
          `${API}/hustlemeet-circles/host/${c.id}/events`,
          { headers: authHeader(), withCredentials: true });
        if (!cancelled) setEvents(data?.events || []);
      } catch {
        if (!cancelled) setEvents([]);
      }
    })();
    return () => { cancelled = true; };
  }, [c.id]);

  return (
    <div
      data-testid={`host-circle-row-${c.id}`}
      className="rounded-2xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-slate-950 p-5 mb-4"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-black text-lg">{c.name}</h3>
            <StatusPill status={c.status}/>
          </div>
          {c.tagline && <p className="text-xs text-stone-500">{c.tagline}</p>}
          <div className="mt-2 flex flex-wrap gap-3 text-[11px] text-stone-500">
            <span className="inline-flex items-center gap-1">
              <MapPin size={11}/> {c.venue_address || c.vendor_name}
            </span>
            <span>Cadence: <strong>{c.cadence}</strong></span>
            <span>Table: <strong>{c.default_party_size}</strong></span>
          </div>
        </div>
        <Link
          to={`/circles/${c.slug || c.id}`}
          className="text-xs font-bold text-amber-500 inline-flex items-center gap-1 hover:underline"
          data-testid={`link-view-circle-${c.id}`}
        >
          View public <ArrowRight size={12}/>
        </Link>
      </div>

      {c.status === "active" && (
        <>
          <button
            type="button"
            data-testid={`btn-toggle-publish-${c.id}`}
            onClick={() => setShow(!show)}
            className="mt-4 text-xs font-bold text-amber-500 inline-flex items-center gap-1 hover:underline"
          >
            {show ? "Hide" : "Publish dinner"} {show ? "↑" : "↓"}
          </button>
          {show && (
            <PublishDinnerForm
              circle={c}
              onPublished={(ev) => {
                setEvents((events || []).concat([ev]));
                onEventPublished?.(ev);
                setShow(false);
              }}
            />
          )}
        </>
      )}

      {events && events.length > 0 && (
        <div className="mt-4 pt-4 border-t border-stone-200 dark:border-stone-800">
          <p className="text-[11px] uppercase tracking-widest text-stone-500 font-bold mb-2">
            Recent dinners ({events.length})
          </p>
          <ul className="space-y-1 text-xs text-stone-500">
            {events.slice(0, 5).map((ev) => (
              <li key={ev.id} data-testid={`host-circle-event-${ev.id}`}>
                <strong>{new Date(ev.starts_at).toLocaleString(undefined,
                  { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</strong>
                {" · "}{ev.title}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Sprint 4 — Attach to a Meet group */}
      <AttachMeetGroupPanel circle={c} onCircleUpdated={onCircleUpdated}/>
    </div>
  );
}

export default function HustleMeetHostDashboardPage() {
  const [circles, setCircles] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [err,     setErr]     = useState("");

  useEffect(() => {
    document.title = "HustleMeet · My Circles";
    (async () => {
      try {
        const { data } = await axios.get(
          `${API}/hustlemeet-circles/host/mine`,
          { headers: authHeader(), withCredentials: true });
        setCircles(data?.circles || []);
      } catch (e) {
        setErr(e?.response?.data?.detail || e.message);
      }
    })();
  }, []);

  if (err) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-[#0a0908] flex items-center justify-center px-6">
        <div className="max-w-md text-center" data-testid="host-dashboard-error">
          <p className="text-lg font-black">Please sign in</p>
          <p className="mt-2 text-stone-500 text-sm">{err}</p>
        </div>
      </div>
    );
  }
  if (!circles) {
    return (
      <div data-testid="host-dashboard-loading" className="min-h-screen bg-slate-50 dark:bg-[#0a0908] flex items-center justify-center text-stone-400">
        Loading…
      </div>
    );
  }

  return (
    <div
      data-testid="hustlemeet-host-dashboard-page"
      className="min-h-screen bg-slate-50 text-stone-900 dark:bg-[#0a0908] dark:text-stone-100 transition-colors"
    >
      <PlatformHeader
        brand={{ name: "HustleMeet · My Circles", to: "/meet/host/mine" }}
        showBookCta={false} showCart={false}
      />

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-24">
        <header className="mb-8 flex items-start gap-4">
          <div className="w-12 h-12 rounded-2xl bg-amber-500/10 text-amber-500 flex items-center justify-center">
            <Users size={22}/>
          </div>
          <div className="flex-1">
            <p className="text-amber-500 text-[11px] font-black uppercase tracking-[0.32em]">
              Host workspace
            </p>
            <h1 className="mt-1 text-3xl font-black">My Circles</h1>
            <p className="mt-1 text-xs text-stone-500">
              Create Circles, publish dinner dates, watch your covers fill.
            </p>
          </div>
          <Button
            data-testid="btn-new-circle"
            onClick={() => setShowNew(!showNew)}
            className="rounded-full bg-amber-500 text-slate-950 hover:bg-amber-400 font-black"
          >
            <Plus size={14} className="mr-1"/> New Circle
          </Button>
        </header>

        {showNew && (
          <CreateCircleForm
            onCreated={(c) => { setCircles([c, ...circles]); setShowNew(false); }}
          />
        )}

        {circles.length === 0 && !showNew && (
          <div
            data-testid="host-dashboard-empty"
            className="rounded-2xl border border-dashed border-stone-300 dark:border-stone-700 p-10 text-center"
          >
            <Sparkles size={28} className="mx-auto text-amber-500"/>
            <p className="mt-4 font-semibold">You&apos;re approved as a host — start your first Circle.</p>
            <Button
              onClick={() => setShowNew(true)}
              data-testid="btn-empty-new-circle"
              className="mt-4 rounded-full bg-amber-500 text-slate-950 hover:bg-amber-400 font-black"
            >
              <Plus size={14} className="mr-1"/> Create Circle
            </Button>
          </div>
        )}

        {circles.map((c) => (
          <CircleRow
            key={c.id}
            c={c}
            onCircleUpdated={(patch) =>
              setCircles(circles.map(x =>
                x.id === c.id ? { ...x, ...patch } : x))}
          />
        ))}
      </div>

      <SuiteFooter />
    </div>
  );
}
